import { useCallback } from 'react';
import html2canvas from 'html2canvas';
import { Share } from '@capacitor/share';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';

export const useChartShare = () => {
  const shareChart = useCallback(async (
    chartRef: React.RefObject<HTMLDivElement>,
    chartTitle?: string
  ) => {
    if (!chartRef.current) {
      console.error('Chart reference is not available');
      return;
    }

    try {
      // Generate canvas from the chart element
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: null, // Transparent background
        scale: 2, // Higher resolution
        useCORS: true,
        allowTaint: true,
        logging: false,
        width: chartRef.current.offsetWidth,
        height: chartRef.current.offsetHeight,
      });

      // Convert canvas to blob
      const dataUrl = canvas.toDataURL('image/png', 1.0);
      
      if (Capacitor.isNativePlatform()) {
        // For native platforms, save to filesystem and share
        const fileName = `chart_${Date.now()}.png`;
        const base64Data = dataUrl.split(',')[1];
        
        try {
          // Write file to temporary directory
          const fileResult = await Filesystem.writeFile({
            path: fileName,
            data: base64Data,
            directory: Directory.Cache,
          });

          // Share the file
          await Share.share({
            title: chartTitle || 'Chart',
            text: chartTitle ? `Check out this ${chartTitle}` : 'Check out this chart',
            url: fileResult.uri,
            dialogTitle: 'Share Chart',
          });

        } catch (filesystemError) {
          console.error('Filesystem error:', filesystemError);
        }
      } else {
        // For web platform, create downloadable link
        const link = document.createElement('a');
        link.download = `chart_${Date.now()}.png`;
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Also try to use Web Share API if available
        if (navigator.share) {
          // Convert data URL to blob for sharing
          const response = await fetch(dataUrl);
          const blob = await response.blob();
          const file = new File([blob], `chart_${Date.now()}.png`, { type: 'image/png' });
          
          await navigator.share({
            title: chartTitle || 'Chart',
            text: chartTitle ? `Check out this ${chartTitle}` : 'Check out this chart',
            files: [file],
          });
        }
      }
    } catch (error) {
      console.error('Error sharing chart:', error);
    }
  }, []);

  return { shareChart };
};
